import React, { useEffect, useState, useCallback, useRef } from 'react';
import { getFeuilles, getFeuille, createFeuille, updateFeuille, deleteFeuille, getEmployes, getProjets, genererFeuillesMasse } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useExportExcel, fmtDate } from '../hooks/useExportExcel';

/* ─── Constants ─────────────────────────────────────────────────────────── */
const MOIS_NOM = ['','Janvier','Février','Mars','Avril','Mai','Juin','Juillet',
  'Août','Septembre','Octobre','Novembre','Décembre'];

const CODES = [
  { code:'HT', label:'Heures travaillées',        color:'#1d4ed8', bg:'#eff6ff', border:'#bfdbfe' },
  { code:'HS', label:'Heures supplémentaires',    color:'#b45309', bg:'#fffbeb', border:'#fde68a' },
  { code:'CA', label:'Congés annuels',             color:'#15803d', bg:'#f0fdf4', border:'#bbf7d0' },
  { code:'AM', label:'Arrêts maladies',            color:'#b91c1c', bg:'#fef2f2', border:'#fecaca' },
  { code:'AE', label:'Absences exceptionnelles',  color:'#7c3aed', bg:'#f5f3ff', border:'#ddd6fe' },
  { code:'PE', label:'Permissions exceptionnelles',color:'#0e7490', bg:'#ecfeff', border:'#a5f3fc' },
  { code:'JF', label:'Jours fériés',              color:'#475569', bg:'#f8fafc', border:'#cbd5e1' },
];
const DAY_LABELS = ['Di','Lu','Ma','Me','Je','Ve','Sa'];
const HKEYS = ['heures_ht','heures_hs','heures_ca','heures_am','heures_ae','heures_pe','heures_jf'];
/* Styles compacts du modèle imprimable (§3.42) */
const SM = {
  th: { background:'#0f2644', color:'#fff', padding:'2px 4px', fontSize:9.5,
        border:'1px solid #0f2644', textAlign:'center', fontWeight:700, whiteSpace:'nowrap',
        lineHeight:1.15 },
  td: { border:'1px solid #94a3b8', padding:'1px 5px', fontSize:9.5, lineHeight:1.3 },
};
const now = new Date();

/* ─── Inline Styles ──────────────────────────────────────────────────────── */
const S = {
  /* Layout */
  root: { fontFamily:"'DM Sans', 'Segoe UI', sans-serif", color:'#1e293b', minHeight:'100%' },

  /* Header */
  header: { display:'flex', justifyContent:'space-between', alignItems:'center',
    padding:'20px 28px 0', gap:12 },
  headerLeft: {},
  title: { margin:0, fontSize:22, fontWeight:700, color:'#0f172a', letterSpacing:'-0.4px' },
  subtitle: { margin:'2px 0 0', fontSize:12.5, color:'#64748b' },

  /* Tabs */
  tabBar: { display:'flex', gap:0, padding:'16px 28px 0', borderBottom:'2px solid #e2e8f0',
    marginBottom:20 },
  tab: (active) => ({
    padding:'8px 18px', fontSize:13.5, fontWeight:600, cursor:'pointer', border:'none',
    background:'transparent', color: active ? '#1d4ed8' : '#64748b',
    borderBottom: active ? '2.5px solid #1d4ed8' : '2.5px solid transparent',
    marginBottom:'-2px', borderRadius:'4px 4px 0 0', transition:'all .15s',
  }),

  /* Filter bar */
  filterBar: { display:'flex', gap:8, alignItems:'center' },
  select: { fontSize:13, padding:'7px 10px', border:'1.5px solid #cbd5e1',
    borderRadius:7, color:'#1e293b', background:'#fff', outline:'none', cursor:'pointer' },

  /* Card */
  card: { background:'#fff', borderRadius:12, border:'1px solid #e2e8f0',
    boxShadow:'0 1px 4px rgba(15,23,42,.05)', margin:'0 28px 20px', overflow:'hidden' },
  cardHead: { display:'flex', justifyContent:'space-between', alignItems:'center',
    padding:'14px 20px', borderBottom:'1px solid #f1f5f9' },
  cardTitle: { fontSize:14, fontWeight:700, color:'#0f172a' },
  cardBody: { padding:20 },

  /* Table */
  table: { width:'100%', borderCollapse:'collapse', fontSize:13 },
  th: { padding:'10px 14px', background:'#f8fafc', color:'#475569', fontWeight:600,
    fontSize:12, textAlign:'left', borderBottom:'1px solid #e2e8f0',
    whiteSpace:'nowrap', letterSpacing:'.02em' },
  thCenter: { textAlign:'center' },
  td: { padding:'11px 14px', borderBottom:'1px solid #f1f5f9', color:'#1e293b', verticalAlign:'middle' },
  tdCenter: { textAlign:'center' },
  trHover: { transition:'background .1s' },

  /* Badges */
  badge: (type) => {
    const map = {
      Brouillon:            ['#f1f5f9','#475569'],
      Soumis:               ['#dbeafe','#1d4ed8'],
      Validé:               ['#dcfce7','#15803d'],
      'Validé Comptabilité':['#ede9fe','#6d28d9'],
      Rejeté:               ['#fef2f2','#dc2626'],
    };
    const [bg, color] = map[type] || map.Brouillon;
    return { display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px',
      borderRadius:20, fontSize:11.5, fontWeight:700, background:bg, color };
  },
  dot: (type) => {
    const map = {
      Brouillon:            '#94a3b8',
      Soumis:               '#3b82f6',
      Validé:               '#22c55e',
      'Validé Comptabilité':'#7c3aed',
      Rejeté:               '#ef4444',
    };
    return { width:6, height:6, borderRadius:'50%', background: map[type] || '#94a3b8',
      display:'inline-block' };
  },

  /* Buttons */
  btnPrimary: { padding:'8px 18px', background:'#1d4ed8', color:'#fff', border:'none',
    borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', display:'inline-flex',
    alignItems:'center', gap:6, transition:'background .15s' },
  btnGhost: { padding:'7px 14px', background:'transparent', color:'#475569',
    border:'1.5px solid #cbd5e1', borderRadius:8, fontSize:13, fontWeight:500,
    cursor:'pointer', display:'inline-flex', alignItems:'center', gap:5, transition:'all .15s' },
  btnGreen: { padding:'7px 14px', background:'#16a34a', color:'#fff', border:'none',
    borderRadius:8, fontSize:12.5, fontWeight:600, cursor:'pointer', display:'inline-flex',
    alignItems:'center', gap:5, transition:'background .15s' },
  btnRed: { padding:'7px 14px', background:'#dc2626', color:'#fff', border:'none',
    borderRadius:8, fontSize:12.5, fontWeight:600, cursor:'pointer', transition:'background .15s',
    display:'inline-flex', alignItems:'center', gap:4 },
  btnDanger: { padding:'5px 10px', background:'#fef2f2', color:'#dc2626',
    border:'1px solid #fecaca', borderRadius:6, fontSize:12, fontWeight:600, cursor:'pointer' },
  btnIcon: (variant) => ({
    width:32, height:32, borderRadius:7, border:'none', cursor:'pointer', display:'inline-flex',
    alignItems:'center', justifyContent:'center', fontSize:14,
    background: variant==='view' ? '#eff6ff' : '#fef2f2',
    color: variant==='view' ? '#1d4ed8' : '#dc2626',
    transition:'all .15s',
  }),

  /* Metrics */
  metrics: { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 },
  metric: (color, bg) => ({ padding:'14px 18px', background: bg, border:`1px solid ${color}22`,
    borderRadius:10, borderLeft:`3px solid ${color}` }),
  metricLabel: { fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase',
    letterSpacing:'.06em', marginBottom:4 },
  metricValue: (color) => ({ fontSize:22, fontWeight:800, color }),

  /* Empty */
  empty: { textAlign:'center', padding:'40px 0', color:'#94a3b8', fontSize:14 },

  /* Form */
  formGrid2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 },
  formGrid3: { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:12 },
  fg: { display:'flex', flexDirection:'column', gap:4 },
  label: { fontSize:12, fontWeight:600, color:'#475569' },
  input: { padding:'8px 11px', border:'1.5px solid #e2e8f0', borderRadius:7, fontSize:13,
    color:'#1e293b', outline:'none', transition:'border .15s', background:'#fff' },
  inputNum: { padding:'6px 4px', border:'1px solid #e2e8f0', borderRadius:6, fontSize:12,
    color:'#1e293b', width:46, textAlign:'center', outline:'none', background:'#fff' },
  textarea: { padding:'8px 11px', border:'1.5px solid #e2e8f0', borderRadius:7, fontSize:13,
    color:'#1e293b', resize:'vertical', minHeight:72, outline:'none', fontFamily:'inherit' },

  /* Calendar table */
  tgWrap: { overflowX:'auto', borderRadius:8, border:'1px solid #e2e8f0' },
  tg: { width:'100%', borderCollapse:'collapse', fontSize:12.5 },
  tgTh: (color) => ({ padding:'8px 6px', background:'#f8fafc', textAlign:'center',
    fontWeight:700, color: color||'#475569', borderBottom:'2px solid #e2e8f0',
    whiteSpace:'nowrap', fontSize:12 }),
  tgTd: (isWE) => ({ padding:'4px 3px', background: isWE ? '#f8fafc' : '#fff',
    textAlign:'center', borderBottom:'1px solid #f1f5f9' }),
  tgTdFoot: { padding:'7px 6px', background:'#dcfce7', textAlign:'center',
    fontWeight:700, color:'#15803d', borderTop:'2px solid #bbf7d0', fontSize:12.5 },

  /* Actions bar */
  actBar: { display:'flex', justifyContent:'flex-end', gap:10, padding:'16px 20px',
    borderTop:'1px solid #f1f5f9', marginTop:4 },

  /* Toast */
  toast: (type) => ({
    position:'fixed', bottom:24, right:24, zIndex:9999,
    padding:'12px 20px', borderRadius:10, fontSize:13.5, fontWeight:600,
    boxShadow:'0 8px 24px rgba(0,0,0,.12)',
    background: type==='error' ? '#dc2626' : '#16a34a', color:'#fff',
    animation:'slideIn .25s ease',
  }),
};

/* ─── Toast ──────────────────────────────────────────────────────────────── */
function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={S.toast(type)}>
      {type === 'error' ? '✕ ' : '✓ '}{msg}
    </div>
  );
}

/* ─── Stat Badge ─────────────────────────────────────────────────────────── */
function StatBadge({ status }) {
  return (
    <span style={S.badge(status)}>
      <span style={S.dot(status)} />
      {status}
    </span>
  );
}

/* ─── Confirm Dialog ─────────────────────────────────────────────────────── */
function ConfirmDialog({ msg, onConfirm, onCancel }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,.4)', display:'flex',
      alignItems:'center', justifyContent:'center', zIndex:9000 }}>
      <div style={{ background:'#fff', borderRadius:14, padding:'28px 32px', minWidth:320,
        boxShadow:'0 20px 60px rgba(0,0,0,.2)', textAlign:'center' }}>
        <div style={{ fontSize:32, marginBottom:10 }}>🗑️</div>
        <div style={{ fontSize:15, fontWeight:600, color:'#1e293b', marginBottom:6 }}>Confirmer la suppression</div>
        <div style={{ fontSize:13, color:'#64748b', marginBottom:20 }}>{msg}</div>
        <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
          <button style={S.btnGhost} onClick={onCancel}>Annuler</button>
          <button style={S.btnRed} onClick={onConfirm}>Supprimer</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────────── */
export default function TempsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role !== 'employe';
  const { exporting, exportToExcel } = useExportExcel();

  const [feuilles, setFeuilles] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('list');
  const [detail, setDetail] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, label }
  const [editTarget, setEditTarget] = useState(null); // feuille being edited

  /* ── §3.36 → §3.42 : recherche, génération de masse, export PDF ───────── */
  const [rechF, setRechF]                   = useState('');
  const [generatingMass, setGeneratingMass] = useState(false);
  const [pdfGen, setPdfGen]                 = useState(false);
  const feuilleRef                          = useRef(null);

  const feuillesFiltrees = feuilles.filter(f => {
    const q = rechF.trim().toLowerCase();
    if (!q) return true;
    return [f.nom, f.prenoms, f.matricule, f.superviseur_nom, f.superviseur,
            f.statut, MOIS_NOM[f.mois], String(f.annee)]
      .filter(Boolean).join(' ').toLowerCase().includes(q);
  });

  const sumH = (k) => (detail?.jours || []).reduce((s, j) => s + (parseFloat(j[k]) || 0), 0);

  const handleGenererFeuilles = async () => {
    // §3.47 — régénération : les feuilles existantes sont écrasées
    if (!window.confirm(
      `Générer les feuilles de temps de tous les employés actifs pour `
      + `${MOIS_NOM[filterMois]} ${filterAnnee} ?

`
      + `⚠️ Les feuilles existantes de cette période seront ÉCRASÉES et régénérées `
      + `(heures et sources de financement remises à zéro).
`
      + `Les feuilles déjà validées sont protégées et ne seront pas touchées.`
    )) return;
    setGeneratingMass(true);
    try {
      const r = await genererFeuillesMasse({
        mois: Number(filterMois), annee: Number(filterAnnee), ecraser: true,
      });
      const d = r?.data || {};
      const parts = [`${d.crees ?? 0} créée(s)`];
      if (d.remplacees) { parts.push(`${d.remplacees} écrasée(s)`); }
      if (d.protegees)  { parts.push(`${d.protegees} protégée(s) car validée(s)`); }
      if (d.ignorees)   { parts.push(`${d.ignorees} ignorée(s)`); }
      showToast(parts.join(' · '));
      load();
    } catch (e) {
      showToast(e?.response?.data?.error || 'Erreur lors de la génération', 'error');
    } finally { setGeneratingMass(false); }
  };

  const handleFeuillePDF = async () => {
    if (!feuilleRef.current) return;
    setPdfGen(true);
    try {
      const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
        import('jspdf'), import('html2canvas'),
      ]);
      const canvas = await html2canvas(feuilleRef.current, {
        scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false,
      });
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const img = canvas.toDataURL('image/png');
      // §3.44 — la feuille tient obligatoirement sur UNE seule page A4 paysage
      const marge  = 4;
      const dispoW = pw - marge * 2;
      const dispoH = ph - marge * 2;
      const ratio  = Math.min(dispoW / canvas.width, dispoH / canvas.height);
      const w = canvas.width  * ratio;
      const h = canvas.height * ratio;
      pdf.addImage(img, 'PNG', (pw - w) / 2, (ph - h) / 2, w, h);
      pdf.save(`FeuilleTemps_${(detail?.nom||'').replace(/\s+/g,'_')}_${(detail?.prenoms||'').replace(/\s+/g,'_')}_${MOIS_NOM[detail?.mois]}_${detail?.annee}.pdf`);
    } catch (e) {
      showToast('Erreur PDF : vérifiez que jspdf et html2canvas sont installés', 'error');
    } finally { setPdfGen(false); }
  };

  const [filterMois, setFilterMois] = useState(now.getMonth() + 1);
  const [filterAnnee, setFilterAnnee] = useState(now.getFullYear());
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - 2 + i);

  // ── Tri ──────────────────────────────────────────────────────────────────
  const [sortCol, setSortCol]  = useState('nom');
  const [sortDir, setSortDir]  = useState('asc'); // 'asc' | 'desc'

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sortedFeuilles = [...feuillesFiltrees].sort((a, b) => {
    let va, vb;
    switch (sortCol) {
      case 'nom':
        va = `${a.nom} ${a.prenoms}`.toLowerCase();
        vb = `${b.nom} ${b.prenoms}`.toLowerCase();
        break;
      case 'superviseur':
        va = (a.superviseur_nom || a.superviseur || '').toLowerCase();
        vb = (b.superviseur_nom || b.superviseur || '').toLowerCase();
        break;
      case 'periode':
        va = a.annee * 100 + a.mois;
        vb = b.annee * 100 + b.mois;
        break;
      case 'solde_ca':
        va = parseFloat(a.solde_ca) || 0;
        vb = parseFloat(b.solde_ca) || 0;
        break;
      case 'statut':
        const order = { Brouillon:0, Soumis:1, Validé:2, 'Validé Comptabilité':3, Rejeté:4 };
        va = order[a.statut] ?? 99;
        vb = order[b.statut] ?? 99;
        break;
      default:
        va = a.id; vb = b.id;
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });

  const blankForm = () => ({
    employe_id:'', nom:'', prenoms:'', matricule:'', superviseur:'',
    mois: now.getMonth() + 1, annee: now.getFullYear(),
    solde_ca:0, solde_am:0, solde_ae:0, acquis_ca:2.2,
    commentaire:'', statut:'Brouillon',
    jours: buildJours(now.getMonth() + 1, now.getFullYear()),
    sources: [{ nom_projet:'', projet_id:'', pourcentage:100 }],
  });
  const [form, setForm] = useState(blankForm);

  const [projets, setProjets] = useState([]);
  const showToast = (msg, type = 'success') => setToast({ msg, type });

  /* Load list */
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await getFeuilles({ mois: filterMois, annee: filterAnnee }); setFeuilles(Array.isArray(r.data) ? r.data : (r.data?.data || [])); }
    catch {}
    setLoading(false);
  }, [filterMois, filterAnnee]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getEmployes({ statut:'Actif', limit:200 }).then(r => setEmployes(r.data.data || []));
  }, [isAdmin]);

  useEffect(() => {
    getProjets().then(r => setProjets(Array.isArray(r.data) ? r.data : (r.data?.data || [])));
  }, []);

  /* Helpers */
  function buildJours(mois, annee) {
    const d = new Date(annee, mois - 1, 1); const jours = [];
    while (d.getMonth() === mois - 1) {
      // Utiliser les composants locaux pour éviter tout décalage UTC
      const yyyy = d.getFullYear();
      const mm   = String(d.getMonth() + 1).padStart(2, '0');
      const dd   = String(d.getDate()).padStart(2, '0');
      const ds   = `${yyyy}-${mm}-${dd}`;
      const dow  = d.getDay(); const isWE = dow === 0 || dow === 6;
      jours.push({ jour:ds, dow, isWE, ht:isWE?0:8, hs:0, ca:0, am:0, ae:0, pe:0, jf:0 });
      d.setDate(d.getDate() + 1);
    }
    return jours;
  }

  const updJour = (i, k, v) => setForm(f => {
    const jours = [...f.jours]; jours[i] = { ...jours[i], [k]: parseFloat(v) || 0 };
    return { ...f, jours };
  });
  const updSource = (i, k, v) => setForm(f => {
    const sources = [...f.sources]; sources[i] = { ...sources[i], [k]: v };
    return { ...f, sources };
  });

  const totalOf = (key) => form.jours.reduce((s, j) => s + (j[key] || 0), 0);

  /* Open handlers */
  const openCreate = () => { setEditTarget(null); setForm(blankForm()); setSubTab('form'); };

  const openEdit = async (id) => {
    try {
      const r = await getFeuille(id); const f = r.data;
      // Fix getDay() UTC : T12:00:00 évite le décalage de fuseau horaire
      const rawJours = f.jours || [];
      const jours = rawJours.length > 0
        ? rawJours.map(j => {
            const dow = new Date(j.jour + 'T12:00:00').getDay();
            return {
              jour: j.jour, dow, isWE: [0,6].includes(dow),
              ht: parseFloat(j.heures_ht)||0, hs: parseFloat(j.heures_hs)||0,
              ca: parseFloat(j.heures_ca)||0, am: parseFloat(j.heures_am)||0,
              ae: parseFloat(j.heures_ae)||0, pe: parseFloat(j.heures_pe)||0,
              jf: parseFloat(j.heures_jf)||0,
            };
          })
        // Fallback : feuille sans jours enregistrés → générer le mois complet
        : buildJours(f.mois, f.annee);
      setForm({
        employe_id: f.employe_id, superviseur: f.superviseur ? String(f.superviseur) : '',
        mois: f.mois, annee: f.annee, solde_ca: f.solde_ca||0,
        solde_am: f.solde_am||0, solde_ae: f.solde_ae||0, acquis_ca: f.acquis_ca||2.2,
        commentaire: f.commentaire||'', statut: f.statut||'Brouillon',
        jours, sources: f.sources || [{ nom_projet:'', projet_id:'', pourcentage:100 }],
      });
      setEditTarget(f);
      setSubTab('form');
    } catch { showToast('Erreur lors du chargement','error'); }
  };

  const openDetail = async (id) => {
    try { const r = await getFeuille(id); setDetail(r.data); setSubTab('detail'); } catch {}
  };

  /* Save */
  const handleSave = async (statut) => {
    // ── Validation préalable ─────────────────────────────────
    if (!form.employe_id && isAdmin) {
      showToast('Veuillez sélectionner un employé.', 'error'); return;
    }
    // Vérifier doublon côté client avant d'appeler l'API
    if (!editTarget) {
      const doublon = feuilles.find(f =>
        String(f.employe_id) === String(form.employe_id) &&
        Number(f.mois)  === Number(form.mois) &&
        Number(f.annee) === Number(form.annee)
      );
      if (doublon) {
        showToast(
          `Une feuille existe déjà pour cet employé en ${MOIS_NOM[form.mois]} ${form.annee}. ` +
          `Utilisez « Modifier » sur la feuille existante.`,
          'error'
        );
        return;
      }
    }
    setSaving(true);
    try {
      if (editTarget) {
        await updateFeuille(editTarget.id, { ...form, statut });
        showToast('Feuille mise à jour');
      } else {
        await createFeuille({ ...form, statut });
        showToast(statut === 'Soumis' ? 'Feuille soumise' : 'Brouillon sauvegardé');
      }
      setSubTab('list'); load();
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.error || '';
      if (status === 409 || serverMsg.includes('Duplicate') || serverMsg.includes('uk_feuille')) {
        showToast(
          `Doublon détecté : une feuille existe déjà pour cet employé en ${MOIS_NOM[form.mois]} ${form.annee}.`,
          'error'
        );
      } else {
        showToast(serverMsg || "Erreur lors de l'enregistrement", 'error');
      }
    }
    setSaving(false);
  };

  /* Delete */
  const handleDelete = (f) => {
    setConfirmDelete({ id: f.id, label: `${f.nom} ${f.prenoms} — ${MOIS_NOM[f.mois]} ${f.annee}` });
  };
  const doDelete = async () => {
    if (!confirmDelete) return;
    try { await deleteFeuille(confirmDelete.id); showToast('Feuille supprimée'); load(); }
    catch { showToast('Erreur lors de la suppression', 'error'); }
    setConfirmDelete(null);
  };

  /* Validate */
  const handleValidate = async (id) => {
    try {
      await updateFeuille(id, { statut:'Validé' });
      showToast('Feuille validée'); load();
    } catch { showToast('Erreur', 'error'); }
  };

  /* Calendar grouping */
  const weeks = [];
  let week = [];
  form.jours.forEach(j => {
    week.push(j);
    if (j.dow === 6 || j === form.jours[form.jours.length - 1]) { weeks.push(week); week = []; }
  });

  /* ── Render ── */
  return (
    <div style={S.root}>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      {confirmDelete && (
        <ConfirmDialog
          msg={`Supprimer la feuille de ${confirmDelete.label} ?`}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Header */}
      <div style={S.header}>
        <div style={S.headerLeft}>
          <h2 style={S.title}>Feuilles de temps mensuelles</h2>
          <p style={S.subtitle}>Saisie des heures travaillées — GFM3 Staff Local</p>
        </div>
        <div style={S.filterBar}>
          <select style={S.select} value={filterMois} onChange={e => setFilterMois(e.target.value)}>
            {MOIS_NOM.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select style={S.select} value={filterAnnee} onChange={e => setFilterAnnee(e.target.value)}>
            {years.map(y => <option key={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Tab bar */}
      <div style={S.tabBar}>
        {[
          { key:'list', label:'📋 Liste des feuilles' },
          { key:'form', label: editTarget ? '✏️ Modifier la feuille' : '➕ Nouvelle feuille' },
          ...(detail ? [{ key:'detail', label:'🔍 Détail' }] : []),
        ].map(t => (
          <button key={t.key} style={S.tab(subTab === t.key)}
            onClick={t.key === 'form' ? (subTab === 'form' ? undefined : openCreate) : () => setSubTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ LIST ══ */}
      {subTab === 'list' && (
        <div style={S.card}>
          <div style={S.cardHead}>
            <span style={S.cardTitle}>
              {feuillesFiltrees.length} feuille{feuillesFiltrees.length !== 1 ? 's' : ''} —&nbsp;
              {MOIS_NOM[filterMois]} {filterAnnee}
            </span>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap:'wrap' }}>
            <input
              value={rechF}
              onChange={e => setRechF(e.target.value)}
              placeholder="🔍 Rechercher (nom, matricule, superviseur, statut…)"
              style={{ padding:'8px 12px', border:'1px solid #cbd5e1', borderRadius:8,
                       fontSize:13, minWidth:280, fontFamily:'inherit', outline:'none' }}
            />
            {isAdmin && (
              <button
                onClick={handleGenererFeuilles}
                disabled={generatingMass}
                style={{ ...S.btnPrimary, background:'#7c3aed', opacity: generatingMass ? .7 : 1 }}
                title="Générer / régénérer les feuilles de temps de tous les employés actifs">
                {generatingMass ? '⏳ Génération…' : '⚡ Générer / régénérer'}
              </button>
            )}
            <button
              onClick={() => exportToExcel({
                rows: feuilles,
                filename: `GFM3_FeuillesTemps_${filterMois}-${filterAnnee}.xlsx`,
                sheetName: 'Feuilles de temps',
                title: `Feuilles de temps — ${MOIS_NOM[filterMois]} ${filterAnnee}`,
                columns: [
                  { key: 'prenoms',        label: 'Prénoms',      width: 20 },
                  { key: 'nom',            label: 'Nom',          width: 20 },
                  { key: 'mois',           label: 'Mois',         width: 10 },
                  { key: 'annee',          label: 'Année',        width: 10 },
                  { key: 'total_heures',   label: 'Heures',       width: 12 },
                  { key: 'total_jours',    label: 'Jours',        width: 12 },
                  { key: 'statut',         label: 'Statut',       width: 16 },
                  { key: 'date_soumission',label: 'Soumis le',    width: 18, format: fmtDate },
                  { key: 'created_at',     label: 'Créé le',      width: 16, format: fmtDate },
                ],
              })}
              disabled={exporting || !feuilles.length}
              style={{
                ...S.btnPrimary,
                background: '#2e7d32',
                opacity: exporting ? .7 : 1,
                cursor: feuilles.length ? 'pointer' : 'not-allowed',
              }}
            >
              {exporting ? '⏳' : '📊'} Exporter Excel
            </button>
            <button style={S.btnPrimary} onClick={openCreate}>
              <span style={{ fontSize:16 }}>+</span> Nouvelle feuille
            </button>
          </div>
          </div>

          {loading ? (
            <div style={S.empty}>
              <span style={{ fontSize:28 }}>⏳</span>
              <div style={{ marginTop:8 }}>Chargement…</div>
            </div>
          ) : feuilles.length === 0 ? (
            <div style={S.empty}>
              <span style={{ fontSize:36 }}>📄</span>
              <div style={{ marginTop:8 }}>Aucune feuille pour cette période</div>
              <button style={{ ...S.btnPrimary, margin:'12px auto 0', display:'flex' }} onClick={openCreate}>
                + Créer la première feuille
              </button>
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {isAdmin && (
                      <th style={{ ...S.th, cursor:'pointer', userSelect:'none' }} onClick={() => toggleSort('nom')}>
                        Employé(e) {sortCol==='nom' ? (sortDir==='asc' ? ' ↑' : ' ↓') : ' ⇅'}
                      </th>
                    )}
                    <th style={{ ...S.th, cursor:'pointer', userSelect:'none' }} onClick={() => toggleSort('periode')}>
                      Période {sortCol==='periode' ? (sortDir==='asc' ? ' ↑' : ' ↓') : ' ⇅'}
                    </th>
                    <th style={{ ...S.th, cursor:'pointer', userSelect:'none' }} onClick={() => toggleSort('superviseur')}>
                      Superviseur {sortCol==='superviseur' ? (sortDir==='asc' ? ' ↑' : ' ↓') : ' ⇅'}
                    </th>
                    <th style={{ ...S.th, cursor:'pointer', userSelect:'none' }} onClick={() => toggleSort('solde_ca')}>
                      Solde CA {sortCol==='solde_ca' ? (sortDir==='asc' ? ' ↑' : ' ↓') : ' ⇅'}
                    </th>
                    <th style={{ ...S.th, cursor:'pointer', userSelect:'none' }} onClick={() => toggleSort('statut')}>
                      Statut {sortCol==='statut' ? (sortDir==='asc' ? ' ↑' : ' ↓') : ' ⇅'}
                    </th>
                    <th style={{ ...S.th, ...S.thCenter }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedFeuilles.map(f => (
                    <tr key={f.id} style={S.trHover}
                      onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      {isAdmin && (
                        <td style={S.td}>
                          <div style={{ fontWeight:700, fontSize:13.5 }}>{f.nom} {f.prenoms}</div>
                          <div style={{ fontSize:11, color:'#94a3b8', marginTop:1 }}>{f.matricule}</div>
                        </td>
                      )}
                      <td style={S.td}>
                        <span style={{ fontWeight:700, color:'#0f172a' }}>{MOIS_NOM[f.mois]}</span>
                        <span style={{ color:'#64748b', marginLeft:4 }}>{f.annee}</span>
                      </td>
                      <td style={{ ...S.td, color:'#475569', fontSize:12.5 }}>{f.superviseur_nom || f.superviseur || '—'}</td>
                      <td style={S.td}>
                        <span style={{ fontWeight:600, color:'#15803d', fontSize:13 }}>
                          {parseFloat(f.solde_ca) || 0} j
                        </span>
                      </td>
                      <td style={S.td}><StatBadge status={f.statut} /></td>
                      <td style={{ ...S.td, ...S.tdCenter }}>
                        <div style={{ display:'flex', gap:6, justifyContent:'center', alignItems:'center' }}>
                          {/* View */}
                          <button style={S.btnIcon('view')} title="Voir le détail"
                            onClick={() => openDetail(f.id)}>👁</button>
                          {/* Edit */}
                          {f.statut !== 'Validé' && (
                            <button style={S.btnIcon('edit')} title="Modifier"
                              onClick={() => openEdit(f.id)}
                              onMouseEnter={e => e.currentTarget.style.background='#fef9c3'}
                              onMouseLeave={e => e.currentTarget.style.background='#fef2f2'}>
                              ✏️
                            </button>
                          )}
                          {/* Validate */}
                          {isAdmin && f.statut !== 'Validé' && (
                            <button style={{ ...S.btnGreen, padding:'5px 10px', fontSize:12 }}
                              onClick={() => handleValidate(f.id)}>
                              ✓ Valider
                            </button>
                          )}
                          {/* Delete */}
                          <button style={S.btnIcon('del')} title="Supprimer"
                            onClick={() => handleDelete(f)}>🗑</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══ FORM (Create / Edit) ══ */}
      {subTab === 'form' && (
        <>
          {/* Identité */}
          <div style={S.card}>
            <div style={S.cardHead}>
              <span style={S.cardTitle}>
                {editTarget ? `✏️ Modifier — ${editTarget.nom} ${editTarget.prenoms}` : '➕ Nouvelle feuille de temps'}
              </span>
              <button style={S.btnGhost} onClick={() => setSubTab('list')}>← Retour</button>
            </div>
            <div style={S.cardBody}>
              <div style={S.formGrid2}>
                {isAdmin && (
                  <div style={S.fg}>
                    <label style={S.label}>Employé(e)</label>
                    <select style={S.input} value={form.employe_id}
                      onChange={e => {
                        const emp = employes.find(x => String(x.id) === e.target.value);
                        setForm(f => ({ ...f, employe_id: e.target.value,
                          nom: emp?.nom||'', prenoms: emp?.prenoms||'', matricule: emp?.matricule||'',
                          // Pré-remplit le superviseur avec le supérieur hiérarchique désigné
                          // sur la fiche employé, reste modifiable ci-dessous.
                          superviseur: emp?.superieur_id ? String(emp.superieur_id) : '' }));
                      }}>
                      <option value="">— Choisir —</option>
                      {employes.map(e => (
                        <option key={e.id} value={e.id}>{e.nom} {e.prenoms} ({e.matricule})</option>
                      ))}
                    </select>
                  </div>
                )}
                <div style={S.fg}>
                  <label style={S.label}>Superviseur</label>
                  <select style={S.input} value={form.superviseur}
                    onChange={e => setForm(f => ({ ...f, superviseur: e.target.value }))}>
                    <option value="">— Choisir un superviseur —</option>
                    {/* Option legacy : valeur texte libre déjà en base (ex: "Coordinateur Général") */}
                    {form.superviseur && isNaN(Number(form.superviseur)) &&
                      !employes.some(emp => String(emp.id) === String(form.superviseur)) && (
                      <option value={form.superviseur}>{form.superviseur} ⚠️ à mettre à jour</option>
                    )}
                    {employes.map(emp => (
                      <option key={emp.id} value={String(emp.id)}>
                        {emp.nom} {emp.prenoms} ({emp.matricule})
                      </option>
                    ))}
                  </select>
                  {form.superviseur && isNaN(Number(form.superviseur)) && (
                    <span style={{ fontSize:11, color:'#f59e0b', marginTop:2 }}>
                      ⚠️ Ancienne valeur — veuillez sélectionner un employé dans la liste
                    </span>
                  )}
                </div>
              </div>
              <div style={S.formGrid2}>
                <div style={S.fg}>
                  <label style={S.label}>Mois</label>
                  <select style={S.input} value={form.mois} onChange={e => {
                    const m = parseInt(e.target.value);
                    setForm(f => ({ ...f, mois: m, jours: buildJours(m, f.annee) }));
                  }}>
                    {MOIS_NOM.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
                  </select>
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Année</label>
                  <select style={S.input} value={form.annee} onChange={e => {
                    const a = parseInt(e.target.value);
                    setForm(f => ({ ...f, annee: a, jours: buildJours(f.mois, a) }));
                  }}>
                    {years.map(y => <option key={y}>{y}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Calendrier */}
          <div style={S.card}>
            <div style={S.cardHead}>
              <span style={S.cardTitle}>📅 Calendrier — {MOIS_NOM[form.mois]} {form.annee}</span>
              <span style={{ fontSize:12, color:'#64748b' }}>
                HT: <b style={{color:'#1d4ed8'}}>{totalOf('ht')}h</b> &nbsp;
                HS: <b style={{color:'#b45309'}}>{totalOf('hs')}h</b> &nbsp;
                CA: <b style={{color:'#15803d'}}>{totalOf('ca')}h</b>
              </span>
            </div>
            <div style={{ ...S.cardBody, paddingTop:12 }}>
              <div style={S.tgWrap}>
                <table style={S.tg}>
                  <thead>
                    <tr>
                      <th style={S.tgTh()}>S.</th>
                      <th style={S.tgTh()}>J.</th>
                      <th style={S.tgTh()}>Date</th>
                      {CODES.map(c => (
                        <th key={c.code} style={S.tgTh(c.color)}>
                          {c.code}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {weeks.map((wk, wi) => wk.map((j, ji) => (
                      <tr key={j.jour}>
                        {ji === 0 && (
                          <td rowSpan={wk.length} style={{ ...S.tgTd(false), color:'#94a3b8',
                            fontSize:11, fontWeight:700, minWidth:26 }}>
                            S{wi+1}
                          </td>
                        )}
                        <td style={{ ...S.tgTd(j.isWE), fontWeight:700, fontSize:12,
                          color: j.isWE ? '#cbd5e1' : '#1e293b' }}>
                          {DAY_LABELS[j.dow]}
                        </td>
                        <td style={{ ...S.tgTd(j.isWE), fontFamily:'monospace', fontSize:11,
                          color: j.isWE ? '#cbd5e1' : '#475569', whiteSpace:'nowrap' }}>
                          {j.jour.slice(5)}
                        </td>
                        {['ht','hs','ca','am','ae','pe','jf'].map((k, ki) => {
                          const ci = form.jours.indexOf(j);
                          const disabled = j.isWE && k === 'ht';
                          return (
                            <td key={k} style={S.tgTd(j.isWE)}>
                              <input type="number" value={j[k] || ''} min="0" max="24" step="0.5"
                                placeholder="0" disabled={disabled}
                                onChange={e => updJour(ci, k, e.target.value)}
                                style={{ ...S.inputNum,
                                  background: disabled ? '#f1f5f9' : j[k] > 0 ? CODES[ki].bg : '#fff',
                                  borderColor: j[k] > 0 ? CODES[ki].border : '#e2e8f0',
                                  color: j[k] > 0 ? CODES[ki].color : '#94a3b8',
                                  opacity: disabled ? .4 : 1,
                                }} />
                            </td>
                          );
                        })}
                      </tr>
                    )))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={3} style={{ ...S.tgTdFoot, textAlign:'right', paddingRight:12 }}>
                        TOTAUX
                      </td>
                      {['ht','hs','ca','am','ae','pe','jf'].map(k => (
                        <td key={k} style={S.tgTdFoot}>
                          {form.jours.reduce((s, j) => s + (parseFloat(j[k]) || 0), 0) || '—'}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Sources financement */}
          <div style={S.card}>
            <div style={S.cardHead}>
              <span style={S.cardTitle}>💼 Sources de financement</span>
              <button style={S.btnGhost} onClick={() =>
                setForm(f => ({ ...f, sources: [...f.sources, { nom_projet:'', projet_id:'', pourcentage:0 }] }))}>
                + Ajouter
              </button>
            </div>
            <div style={S.cardBody}>
              {form.sources.map((s, i) => (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'1fr 140px 40px',
                  gap:10, marginBottom:10, background:'#f8fafc', padding:'10px 12px',
                  borderRadius:8, border:'1px solid #e2e8f0', alignItems:'flex-end' }}>
                  <div style={S.fg}>
                    <label style={S.label}>Projet / Activité</label>
                    <select style={S.input} value={s.projet_id || ''}
                      onChange={e => {
                        const proj = projets.find(p => String(p.id) === e.target.value);
                        updSource(i, 'projet_id', e.target.value);
                        updSource(i, 'nom_projet', proj ? proj.nom : '');
                      }}>
                      <option value="">— Sélectionner un projet —</option>
                      {projets.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.nom}{p.bailleur ? ` — ${p.bailleur}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={S.fg}>
                    <label style={S.label}>% du temps</label>
                    <input style={S.input} type="number" value={s.pourcentage}
                      min="0" max="100" onChange={e => updSource(i, 'pourcentage', parseFloat(e.target.value)||0)} />
                  </div>
                  <button style={{ ...S.btnDanger, marginBottom:1 }}
                    onClick={() => setForm(f => ({ ...f, sources: f.sources.filter((_, j) => j !== i) }))}>
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Soldes + Commentaire */}
          <div style={S.card}>
            <div style={S.cardHead}>
              <span style={S.cardTitle}>🏖 Soldes de congés & Commentaire</span>
            </div>
            <div style={S.cardBody}>
              <div style={S.formGrid3}>
                {[['solde_ca','Solde CA (j)'],['solde_am','Solde AM (j)'],['solde_ae','Solde AE (j)']].map(([k,lbl]) => (
                  <div key={k} style={S.fg}>
                    <label style={S.label}>{lbl}</label>
                    <input style={S.input} type="number" step="0.5" value={form[k]}
                      onChange={e => setForm(f => ({ ...f, [k]: parseFloat(e.target.value)||0 }))} />
                  </div>
                ))}
              </div>
              <div style={S.fg}>
                <label style={S.label}>Commentaire</label>
                <textarea style={S.textarea} value={form.commentaire}
                  onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))}
                  placeholder="Observations, notes du collaborateur…" />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ ...S.card, ...S.actBar }}>
            <button style={S.btnGhost} onClick={() => setSubTab('list')}>Annuler</button>
            <button style={{ ...S.btnGhost, borderColor:'#6366f1', color:'#6366f1' }}
              onClick={() => handleSave('Brouillon')} disabled={saving}>
              💾 Enregistrer brouillon
            </button>
            <button style={S.btnPrimary} onClick={() => handleSave('Soumis')} disabled={saving}>
              {saving ? '⏳ Enregistrement…' : '📤 Soumettre la feuille'}
            </button>
          </div>
        </>
      )}

      {/* ══ DETAIL ══ */}
      {subTab === 'detail' && detail && (
        <>
          <div style={{ display:'flex', gap:8, marginBottom:12, justifyContent:'flex-end' }}>
            {isAdmin && detail.statut !== 'Validé' && (
              <button style={S.btnGreen}
                onClick={() => updateFeuille(detail.id, { statut:'Validé' }).then(() => {
                  showToast('Feuille validée'); setSubTab('list'); load();
                })}>
                ✓ Valider
              </button>
            )}
            <button style={S.btnPrimary} onClick={handleFeuillePDF} disabled={pdfGen}>
              {pdfGen ? '⏳ Génération…' : '📄 Générer PDF'}
            </button>
            <button style={S.btnGhost} onClick={() => setSubTab('list')}>← Retour</button>
          </div>

          {/* §3.45 — boîte au format A4 paysage : le contenu remplit la page */}
          <div ref={feuilleRef} style={{ background:'#fff', padding:'14px 18px',
                 border:'1px solid #e2e8f0', borderRadius:10, fontSize:11, color:'#111',
                 fontFamily:"'DM Sans',Arial,sans-serif",
                 width:1122, minHeight:770, boxSizing:'border-box', margin:'0 auto',
                 display:'flex', flexDirection:'column' }}>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start',
                          borderBottom:'2px solid #0f2644', paddingBottom:10, marginBottom:10 }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <img src="/logo_gfm3.png" alt="GFM3" style={{ height:52, width:'auto', borderRadius:4 }} />
                <div>
                  <div style={{ fontWeight:800, fontSize:14, color:'#0f2644' }}>GFM3 — Génération Femme du Troisième Millénaire</div>
                  <div style={{ fontSize:10, color:'#475569', lineHeight:1.6, marginTop:2 }}>
                    Adresse : 27 BP 283 Abidjan 27 | Siège social : Abidjan Angré 7ème tranche<br />
                    N°CNPS : 159633 | N°Contribuable : 0913190U<br />
                    Email : gfm3@yahoo.fr | Téléphone : 27 22 52 20 65
                  </div>
                </div>
              </div>
              <div style={{ textAlign:'right' }}>
                <div style={{ background:'#0f2644', color:'#fff', fontWeight:800, fontSize:12,
                              padding:'6px 12px', borderRadius:6, letterSpacing:.5, whiteSpace:'nowrap' }}>
                  FEUILLE DE TEMPS
                </div>
                <div style={{ fontSize:10.5, color:'#475569', marginTop:6, lineHeight:1.6 }}>
                  Mois : {MOIS_NOM[detail.mois]} {detail.annee}<br />
                  Date d'édition : {new Date().toLocaleDateString('fr-FR')}
                </div>
              </div>
            </div>

            <div style={{ background:'#c9c9c9', border:'1px solid #7f7f7f', textAlign:'center',
                          padding:'8px 6px', marginBottom:12 }}>
              <div style={{ fontWeight:800, fontSize:15, color:'#111', letterSpacing:.5 }}>STAFF LOCAL</div>
            </div>

            {/* Blocs compacts : identité (gauche) et légende (droite) */}
            <div style={{ display:'grid', gridTemplateColumns:'minmax(0,1fr) 430px', gap:12, alignItems:'start', marginBottom:12 }}>
              <table style={{ borderCollapse:'collapse', width:'100%', tableLayout:'fixed' }}>
                <tbody>
                  {[
                    ['EMPLOYE(E)',      `${detail.nom || ''} ${detail.prenoms || ''}`],
                    ['LIEU DE TRAVAIL', detail.lieu_service || detail.bureau_nom || detail.ville || '—'],
                    ['FONCTION',        detail.fonction || '—'],
                    ['MOIS DE',         MOIS_NOM[detail.mois]],
                    ['ANNEE',           detail.annee],
                    ['MATRICULE',       detail.matricule || '—'],
                  ].map(([k, v]) => (
                    <tr key={k}>
                      <td style={{ border:'1px solid #94a3b8', padding:'2px 7px', fontWeight:700, background:'#f1f5f9', width:150, fontSize:10, whiteSpace:'nowrap' }}>{k}</td>
                      <td style={{ border:'1px solid #94a3b8', padding:'2px 7px', fontSize:10 }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <table style={{ borderCollapse:'collapse', width:'100%', tableLayout:'fixed' }}>
                <tbody>
                  {CODES.map(c => (
                    <tr key={c.code}>
                      <td style={{ border:'1px solid #94a3b8', padding:'1px 7px', fontWeight:800, width:40, color:c.color, fontSize:10 }}>{c.code}:</td>
                      <td style={{ border:'1px solid #94a3b8', padding:'1px 7px', fontSize:10, whiteSpace:'nowrap' }}>{c.label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'minmax(0,1fr) 430px', gap:12, alignItems:'start' }}>
              <table style={{ borderCollapse:'collapse', width:'100%', fontSize:9 }}>
                <thead>
                  <tr>
                    <th style={SM.th}>Semaine</th>
                    <th style={SM.th}>Date</th>
                    <th style={SM.th}>Jour</th>
                    {CODES.map(c => <th key={c.code} style={{ ...SM.th, color:'#fff' }}>{c.code}</th>)}
                    {/* §3.49 — total d'heures travaillées du jour = HT + HS */}
                    <th style={{ ...SM.th, background:'#15803d', border:'1px solid #15803d', width:46 }}>TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    /* §3.50 — colonne « Semaine » fusionnée visuellement.
                       On n'utilise pas rowSpan : html2canvas le rend mal à
                       l'export PDF. À la place, une cellule par ligne dont on
                       masque les bordures internes, avec le libellé placé sur
                       la ligne médiane du groupe (effet de centrage vertical). */
                    const liste  = detail.jours || [];
                    const numSem = (jr) => Math.floor((new Date(jr).getDate() - 1) / 7) + 1;
                    const debut  = {};   // semaine → index de sa 1re ligne
                    const compte = {};   // semaine → nombre de lignes
                    liste.forEach((x, i) => {
                      const s = numSem(x.jour);
                      if (debut[s] === undefined) { debut[s] = i; }
                      compte[s] = (compte[s] || 0) + 1;
                    });

                    return liste.map((j, idx) => {
                    const d   = new Date(j.jour);
                    const num = d.getDate();
                    const sem = numSem(j.jour);
                    const prev = idx > 0 ? numSem(liste[idx - 1].jour) : 0;
                    const suiv = idx < liste.length - 1 ? numSem(liste[idx + 1].jour) : 0;
                    const isWE = [0, 6].includes(d.getDay());
                    const semDebut  = sem !== prev;
                    const semFin    = sem !== suiv;
                    const semMilieu = idx === debut[sem] + Math.floor((compte[sem] - 1) / 2);
                    return (
                      <tr key={j.jour} style={{ background: isWE ? '#f1f5f9' : '#fff' }}>
                        <td style={{ ...SM.td, fontWeight:700, whiteSpace:'nowrap',
                                     textAlign:'center', background:'#eef2f7',
                                     borderTop:    semDebut ? '1px solid #94a3b8' : 'none',
                                     borderBottom: semFin   ? '1px solid #94a3b8' : 'none' }}>
                          {semMilieu ? `Semaine ${sem}` : ' '}
                        </td>
                        <td style={{ ...SM.td, fontFamily:'monospace' }}>{j.jour}</td>
                        <td style={{ ...SM.td, textAlign:'center', color: isWE ? '#94a3b8' : '#334155' }}>{DAY_LABELS[d.getDay()]} {num}</td>
                        {HKEYS.map((k, ki) => (
                          <td key={k} style={{ ...SM.td, textAlign:'center',
                                 color: parseFloat(j[k]) > 0 ? CODES[ki].color : '#cbd5e1',
                                 fontWeight: parseFloat(j[k]) > 0 ? 700 : 400 }}>
                            {parseFloat(j[k]) || ''}
                          </td>
                        ))}
                        {/* §3.49 — TOTAL du jour = HT + HS */}
                        <td style={{ ...SM.td, textAlign:'center', fontWeight:800,
                               background:'#f0fdf4',
                               color: (parseFloat(j.heures_ht) || 0) + (parseFloat(j.heures_hs) || 0) > 0
                                 ? '#15803d' : '#cbd5e1' }}>
                          {((parseFloat(j.heures_ht) || 0) + (parseFloat(j.heures_hs) || 0)) || ''}
                        </td>
                      </tr>
                    );
                    });
                  })()}
                  <tr style={{ background:'#dcfce7', fontWeight:800 }}>
                    <td style={SM.td} colSpan={3}>TOTAUX</td>
                    {HKEYS.map(k => (
                      <td key={k} style={{ ...SM.td, textAlign:'center' }}>{sumH(k) || 0}</td>
                    ))}
                    <td style={{ ...SM.td, textAlign:'center', background:'#bbf7d0', color:'#14532d' }}>
                      {(sumH('heures_ht') + sumH('heures_hs')) || 0}
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
                <table style={{ borderCollapse:'collapse', width:'100%', fontSize:9 }}>
                  <thead>
                    <tr>
                      <th style={{ ...SM.th, width:118, textAlign:'left' }}></th>
                      <th style={SM.th}>Congés Annuels</th>
                      <th style={SM.th}>Arrêts Maladies</th>
                      <th style={SM.th}>Autres Absences</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ ...SM.td, fontWeight:700 }}>Solde actuel (report)</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>{Number(detail.solde_ca || 0)}</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>{Number(detail.solde_am || 0)}</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>{Number(detail.solde_ae || 0)}</td>
                    </tr>
                    <tr>
                      <td style={{ ...SM.td, fontWeight:700 }}>+ Acquis du mois</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>{Number(detail.acquis_ca || 0)}</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>0</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>0</td>
                    </tr>
                    <tr>
                      <td style={{ ...SM.td, fontWeight:700 }}>− Pris dans le mois</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>{(sumH('heures_ca') / 8) || 0}</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>{(sumH('heures_am') / 8) || 0}</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>{((sumH('heures_ae') + sumH('heures_pe')) / 8) || 0}</td>
                    </tr>
                    <tr style={{ background:'#dcfce7', fontWeight:800 }}>
                      <td style={SM.td}>NOUVEAU SOLDE</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>{Math.round((Number(detail.solde_ca||0) + Number(detail.acquis_ca||0) - sumH('heures_ca')/8) * 100) / 100}</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>{Math.round((Number(detail.solde_am||0) - sumH('heures_am')/8) * 100) / 100}</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>{Math.round((Number(detail.solde_ae||0) - (sumH('heures_ae')+sumH('heures_pe'))/8) * 100) / 100}</td>
                    </tr>
                  </tbody>
                </table>

                <table style={{ borderCollapse:'collapse', width:'100%', fontSize:9 }}>
                  <thead>
                    <tr>
                      <th style={{ ...SM.th, width:270 }}>Source de Financement</th>
                      <th style={{ ...SM.th, width:110 }}>% Par Projet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.sources || []).map((s, i) => (
                      <tr key={i}>
                        <td style={SM.td}>{s.nom_projet}</td>
                        <td style={{ ...SM.td, textAlign:'center' }}>{Number(s.pourcentage || 0)}%</td>
                      </tr>
                    ))}
                    <tr style={{ background:'#dcfce7', fontWeight:800 }}>
                      <td style={SM.td}>TOTAL</td>
                      <td style={{ ...SM.td, textAlign:'center' }}>
                        {(detail.sources || []).reduce((s, x) => s + (parseFloat(x.pourcentage) || 0), 0)}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1.2fr', gap:20, marginTop:'auto', paddingTop:18, fontSize:10 }}>
              <div>Date : ________________________</div>
              <div>Signature de l'employé : ________________________</div>
              <div style={{ marginTop:20 }}>Date : ________________________</div>
              <div style={{ marginTop:20 }}>Approbation superviseur : ________________________</div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
        @keyframes slideIn { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
}
